import useSWR from 'swr';

import { fetchEndpointData } from '@/utils/fetch.client';
import type { UserRole } from './UserRole';

export interface MeResponseType {
  id: string;
  email: string;
  name: string;
  role: UserRole;
  avatarUrl: string | null;
  lastSeenAt: string;
  /** The user's personal team — the default tenant when no team is selected. */
  personalTeamId: string;
}

export async function fetchMe() {
  return fetchEndpointData<MeResponseType>('/api/v1/user/me', {
    method: 'GET',
  });
}

/**
 * The signed-in user, or `undefined` for a visitor — a session check that never redirects.
 *
 * `AuthProvider` bounces an unauthenticated visitor to `/login`; this is for the pages that must
 * stay put and offer their own sign-in instead (the CLI approval page). Its own cache key keeps a
 * 401 here from settling into the `AuthProvider` entry.
 */
export function useOptionalMe() {
  const { data, isLoading } = useSWR('cli-login:me', fetchMe, {
    shouldRetryOnError: false,
  });
  return { me: data, isLoading };
}

/**
 * The signed-in user behind `AuthProvider`: `me` is `undefined` until the session check resolves,
 * and stays `undefined` for a visitor. `onSuccess` runs after every successful fetch.
 */
export function useMe({ onSuccess }: { onSuccess: () => void }) {
  const { data, isLoading, mutate } = useSWR('/api/v1/user/me', fetchMe, { onSuccess });
  return { me: data, isLoading, mutate };
}
